import { useEffect, useState } from "react";
import clsx from "clsx";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import { releaseGradient } from "./releaseFormat.js";
import styles from "./ReleaseArtwork.module.scss";

// The release cover slot: the real artwork when the release has one, else a DETERMINISTIC identity gradient
// keyed off the release id — never a fictional cover (the born-blank rule). The gradient also stands in while
// the bytes resolve, so the frame never flashes empty and never shows a broken-image glyph.
//
// TASK-022: `ReleaseSummary.coverMediaId` ships on the hub list and the hub ignored it, painting a seeded
// gradient on all four surfaces (spotlight, catalog card, prep chip, smart-link chip) while only the
// command-center hero resolved the real cover. Passing `mediaId` resolves it here, at the ONE component that
// already owns the placeholder — rather than minting a third cover component beside this and `MediaImage`.
//
// Resolving bytes in JS makes `loading="lazy"` on the <img> a LIE on its own — the attribute defers a fetch
// the browser owns, and by the time the element exists we have already downloaded the blob. So `lazy` gates
// the RESOLVE, not the tag: a below-the-fold slot asks for nothing until it is near the viewport. Paired with
// the shared blob cache in `useMedia`, a hub with 8 covers went from 17 requests on mount (plus a full
// re-download per filter toggle) to one request per distinct (media, size) actually scrolled into view.

export interface ReleaseArtworkProps {
  /** The release id — seeds the deterministic gradient so a release always shows the same one. */
  seed: string | null | undefined;
  /** Visual size: `card` (hub grid), `tile` (hub spotlight / hero), `chip` (list rows). */
  size?: "card" | "tile" | "chip";
  className?: string;
  /** Overlaid content (e.g. the type tag on the hub card). */
  children?: React.ReactNode;
  /**
   * A cover image URL — the FALLBACK layer, used only when `mediaId` is absent.
   *
   * Precedence is `mediaId` → `imageUrl` → gradient, and the order is the point: `mediaId` is artwork the band
   * UPLOADED, `imageUrl` is hotlinked from a vendor (Spotify's CDN, on an imported release). A vendor's art
   * must never displace a choice someone made. Callers can pass both and get the right answer.
   *
   * A dead URL costs nothing — the gradient is painted underneath, so a 404 degrades to the born-blank cover
   * rather than a broken tile.
   */
  imageUrl?: string | null;
  /**
   * `coverMediaId` off the release — resolved here (CDN url at go-live, the authenticated `/raw` degrade in
   * dev). Icon-sized slots ask for the `thumb` rendition; the endpoint stands the original in until the
   * worker generates it, so `thumb` degrades rather than fails.
   */
  mediaId?: string | null;
  /**
   * Defer resolving `mediaId` until the slot is within 200px of the viewport (an `IntersectionObserver`),
   * so a catalogue below the fold costs nothing until it is scrolled to. Default true. The above-the-fold
   * spotlight / command-center HERO passes `false` and resolves immediately. No effect on `imageUrl`, which
   * needs no fetch, and none where `IntersectionObserver` is unavailable (jsdom) — there the slot resolves
   * eagerly rather than never.
   */
  lazy?: boolean;
  /** Resolve `mediaId` from this band rather than the mounted one — a surface listing several artists' releases
   *  with no band mounted (the label's All roster release cards, ART-335). */
  bandId?: string | null;
}

export function ReleaseArtwork({
  seed,
  size = "tile",
  className,
  children,
  imageUrl,
  mediaId,
  lazy = true,
  bandId,
}: ReleaseArtworkProps) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  // The band's own upload wins; the hotlink is what fills an empty slot.
  const fallbackUrl = mediaId ? null : (imageUrl ?? null);
  const near = useNearViewport(slot, !lazy || !mediaId || Boolean(fallbackUrl));
  // A `chip`/`card` cover is icon-to-thumbnail sized, so it never needs the original bytes; the hero (`tile`)
  // is the one slot big enough to want the full image.
  const resolved = useMediaObjectUrl(fallbackUrl || !near ? null : mediaId, fallbackUrl, {
    variant: size === "tile" ? "sm" : "thumb",
    bandId,
  });
  const resolvedUrl = resolved.error ? null : resolved.url;

  // A HOTLINKED cover can 404 or 403 at paint time — `useMediaObjectUrl` only reports failures for bytes it
  // fetched itself, and a plain `imageUrl` is handed straight to the browser. Spotify is the live case: some
  // playlist and album art comes back on `image-cdn-ak.spotifycdn.com`, which 403s cross-origin, while
  // `i.scdn.co` loads fine. Without this the frame shows a broken-image glyph over the gradient.
  //
  // Deliberately NOT a hostname allowlist. The legacy tried that and it backfired: blanket-rejecting
  // `spotifycdn.com` blanked the dashboard's covers while another surface rendering the SAME url showed them
  // (`StreamingSection.tsx`'s `usableDirectUrl` comment). Whether a url loads is not knowable from its host —
  // so we let the browser answer, and keep the gradient when it says no.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [resolvedUrl]);
  const url = failed ? null : resolvedUrl;

  return (
    <div
      ref={setSlot}
      className={clsx(styles.art, className)}
      data-size={size}
      style={{ background: releaseGradient(seed) }}
      aria-hidden="true"
    >
      {url && (
        <img
          className={styles.img}
          src={url}
          alt=""
          loading={lazy ? "lazy" : undefined}
          onError={() => setFailed(true)}
        />
      )}
      {children}
    </div>
  );
}

/**
 * True once `node` has come within 200px of the viewport — latched, so a slot scrolled past keeps its cover.
 * `eager` short-circuits it (nothing to defer), as does an environment without `IntersectionObserver`.
 */
function useNearViewport(node: HTMLElement | null, eager: boolean): boolean {
  const [near, setNear] = useState(eager);
  useEffect(() => {
    if (eager) {
      setNear(true);
      return;
    }
    if (near || !node) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [node, near, eager]);
  return near;
}
